/**
 * AiBeautifierModal — turns a raw memorial into a WhatsApp / Facebook-ready
 * ceremonial card in the visual language of Indian white-lily posters.
 *
 * Backend contract: POST /api/memorials/{slug}/beautify
 *   → { ok, card: { title, subtitle, tribute, thanks, verse,
 *                   share_short_url, qr_code_url, portrait, ... } }
 * GET /api/memorials/{slug}/beautify → previously stored card, if any.
 *
 * Two templates:
 *   • cream     — Cream parchment, oval portrait, warm serif (default).
 *   • charcoal  — Deep charcoal + gold-leaf serif, minimalist.
 * Both templates render a Splink QR at the bottom that scans back to the
 * memorial's /celebrate/{slug} page so recipients can leave FB-style
 * comments in the guestbook. The card canvas is fixed to 1080×1350
 * (WhatsApp / Instagram / FB story friendly) and downloaded via
 * html2canvas as a PNG.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Download, Loader2, RefreshCcw, Share2, X, Wand2 } from "lucide-react";
import { http } from "@/lib/api";

const TEMPLATES = [
  {
    id: "cream",
    label: "Cream Parchment",
    swatch: ["#FBE9C6", "#7A4A1F"],
  },
  {
    id: "charcoal",
    label: "Charcoal & Gold",
    swatch: ["#1A1815", "#C7A867"],
  },
];

const LANGUAGES = [
  { code: "auto", label: "Auto" },
  { code: "en",   label: "English" },
  { code: "ml",   label: "മലയാളം" },
  { code: "hi",   label: "हिन्दी" },
  { code: "ta",   label: "தமிழ்" },
];

/**
 * Small helper: convert any image URL (data-URL, /api/files/*, external)
 * into a data-URL the exporter can rasterise even under CORS.
 */
async function toDataUrl(url) {
  if (!url) return null;
  if (url.startsWith("data:")) return url;
  try {
    const r = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!r.ok) throw new Error(`fetch ${r.status}`);
    const blob = await r.blob();
    return await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    // Non-fatal — the card will still render, just without that image.
    console.warn("[AiBeautifier] toDataUrl failed for", url, e);
    return null;
  }
}

// "Kottayam, Kerala, India" from a location dict.
function _formatLocation(loc) {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  return [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
}

export default function AiBeautifierModal({ slug, onClose, initialCard = null, canBeautify = true, initialMemorial = null }) {
  const [template, setTemplate] = useState(initialCard?.template || "cream");
  const [language, setLanguage] = useState(initialCard?.language || "auto");
  const [card,     setCard]     = useState(initialCard);
  const [loading,  setLoading]  = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [portraitDataUrl, setPortraitDataUrl] = useState(null);
  const [qrDataUrl,       setQrDataUrl]       = useState(null);
  const [showFields,      setShowFields]      = useState(false);
  const cardRef = useRef(null);

  // Editable fields — pre-populated from the memorial (via
  // initialMemorial + a public GET on open) so the family can tweak
  // wording, dates, parent names, funeral / cremation info, etc.
  // Any field left blank falls back to the doc value on the backend.
  const [fields, setFields] = useState({
    full_name:       initialMemorial?.full_name       || initialCard?.name          || "",
    date_of_birth:   initialMemorial?.date_of_birth   || initialCard?.date_of_birth || "",
    date_of_death:   initialMemorial?.death_date      || initialCard?.date_of_death || initialCard?.death_date || "",
    location:        _formatLocation(initialMemorial?.location) || initialCard?.location || "",
    parent_names:    initialMemorial?.parents_line    || initialCard?.parent_names  || "",
    description:     initialMemorial?.short_bio       || initialCard?.description   || "",
    funeral_details: initialMemorial?.funeral_details || initialCard?.funeral_details || "",
    footer_message:  initialCard?.footer_message      || "",
  });
  const setField = (k, v) => setFields((p) => ({ ...p, [k]: v }));

  // First open: if no card was passed in, try to hydrate from GET, then
  // auto-request a fresh one so the user isn't staring at a blank slate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/memorials/${slug}/beautify`);
        if (cancelled) return;
        // Backfill editable fields from stored card + memorial doc.
        setFields((prev) => ({
          full_name:       prev.full_name       || data?.name           || data?.card?.name          || "",
          date_of_birth:   prev.date_of_birth   || data?.card?.date_of_birth || "",
          date_of_death:   prev.date_of_death   || data?.death_date     || data?.card?.date_of_death || data?.card?.death_date || "",
          location:        prev.location        || _formatLocation(data?.location) || data?.card?.location || "",
          parent_names:    prev.parent_names    || data?.card?.parent_names  || "",
          description:     prev.description     || data?.card?.description   || "",
          funeral_details: prev.funeral_details || data?.card?.funeral_details || "",
          footer_message:  prev.footer_message  || data?.card?.footer_message || "",
        }));
        if (data?.has_card && data.card) {
          setCard(data.card);
          setTemplate(data.card.template || "cream");
          return;
        }
      } catch (_) { /* fall through to fresh generate */ }
      if (!cancelled && canBeautify && !card) generate({ silent: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Refresh portrait + QR as data-URLs whenever the card changes so
  // html2canvas can rasterise them.
  useEffect(() => {
    (async () => {
      const [p, q] = await Promise.all([
        toDataUrl(card?.portrait),
        toDataUrl(card?.qr_code_url),
      ]);
      setPortraitDataUrl(p);
      setQrDataUrl(q);
    })();
  }, [card?.portrait, card?.qr_code_url]);

  const generate = async ({ silent = false } = {}) => {
    if (!canBeautify) {
      toast.error("Only the submitter or an admin can beautify this memorial.");
      return;
    }
    setLoading(true);
    try {
      // Only send non-empty overrides so the backend falls back to the
      // memorial doc for anything the family left blank.
      const overrides = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => (v ?? "").toString().trim().length > 0)
      );
      const { data } = await http.post(`/memorials/${slug}/beautify`, {
        language,
        template,
        ...overrides,
      });
      if (!data?.ok) throw new Error("Beautify failed");
      setCard(data.card);
      if (!silent) toast.success("Beautiful. Card refreshed with AI.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Beautify failed";
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Pre-rasterise the two flower SVGs into PNG data URLs so
      // html2canvas can composite them reliably (SVG <img> tags render
      // inconsistently across browsers via the standard renderer).
      await _prerasteriseFlorals(cardRef.current);
      // Render at native 1080×1350 for crisp WhatsApp story quality.
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        useCORS: true,
        allowTaint: false,
        scale: 2,
        width:  1080 / 2,
        height: 1350 / 2,
      });
      const link = document.createElement("a");
      link.download = `memorial-${slug}-${template}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Downloaded. Share it on WhatsApp or FB.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export image. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const shareToWhatsApp = () => {
    if (!card) return;
    const url = card.share_short_url || window.location.href;
    const text = `${card.title || "Heartfelt Condolences"} — ${card.name}\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const displayName = fields.full_name || card?.name || "";
  const displayDates = [fields.date_of_birth, fields.date_of_death].filter(Boolean).join(" — ");
  const displayParents = fields.parent_names || "";
  const displayLocation = fields.location || "";
  const displayFuneral  = fields.funeral_details || "";
  const displayDescription = fields.description || card?.tribute || "";
  const displayFooter = fields.footer_message || card?.thanks || "";

  const inkPrimary = template === "charcoal" ? "#F6EBD4" : "#3B1F0A";
  const inkAccent  = template === "charcoal" ? "#C7A867" : "#8A5B2A";
  const inkSoft    = template === "charcoal" ? "#E8D9B7" : "#3B1F0A";

  const CardBody = (
    <div
      ref={cardRef}
      data-testid="beautifier-card-canvas"
      style={{
        width:  540,       // Half of 1080 for on-screen preview; scale:2 exports at 1080×1350
        height: 675,       // Half of 1350
        position: "relative",
        overflow: "hidden",
        borderRadius: 12,
        boxShadow: "0 30px 80px -30px rgba(0,0,0,0.35)",
        ...(template === "cream"    ? cardTheme.cream    : {}),
        ...(template === "charcoal" ? cardTheme.charcoal : {}),
      }}
    >
      <FloralOverlays template={template} />

      {/* ── TOP block: Full name, dates, S/o or D/o parents ── */}
      <div style={{ position: "relative", padding: "26px 40px 0", textAlign: "center", zIndex: 2 }}>
        <div style={{
          fontFamily: "'Playfair Display', 'Georgia', serif",
          fontSize: 15, letterSpacing: 3.8,
          textTransform: "uppercase", color: inkAccent,
        }}>
          {card?.title || "In Loving Memory"}
        </div>
        <div style={{
          marginTop: 8,
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: displayName.length > 34 ? 22 : 28,
          lineHeight: 1.1, color: inkPrimary, wordBreak: "break-word",
        }}>{displayName}</div>
        {displayDates && (
          <div style={{
            marginTop: 4, fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 14, letterSpacing: 1.2, color: inkAccent,
          }}>{displayDates}</div>
        )}
        {displayParents && (
          <div style={{
            marginTop: 2, fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 12, fontStyle: "italic", color: inkAccent,
          }}>{displayParents}</div>
        )}
        {displayLocation && (
          <div style={{
            marginTop: 2, fontSize: 11, letterSpacing: 1.4,
            textTransform: "uppercase", color: inkAccent, opacity: 0.85,
          }}>{displayLocation}</div>
        )}
      </div>

      {/* ── Portrait ── */}
      <div style={{
        position: "relative", zIndex: 2, marginTop: 14,
        display: "flex", justifyContent: "center",
      }}>
        <div style={{
          width: template === "charcoal" ? 180 : 200,
          height: template === "charcoal" ? 210 : 200,
          borderRadius: template === "charcoal" ? "50%/45%" : "50%",
          overflow: "hidden",
          border: template === "charcoal" ? "3px solid #C7A867" : "6px solid #FFF8EC",
          boxShadow: template === "charcoal"
            ? "0 0 0 6px rgba(199,168,103,0.15), 0 20px 40px -20px rgba(0,0,0,0.6)"
            : "0 12px 28px -14px rgba(122,74,31,0.35)",
          background: template === "charcoal" ? "#0f0e0c" : "#FFF8EC",
        }}>
          {portraitDataUrl ? (
            <img
              src={portraitDataUrl}
              alt={displayName || "portrait"}
              crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 56, color: inkAccent,
            }}>{(displayName || "M").slice(0, 1).toUpperCase()}</div>
          )}
        </div>
      </div>

      {/* ── Description / tribute ── */}
      <div style={{
        marginTop: 14, padding: "0 44px",
        textAlign: "center",
        fontFamily: "'Cormorant Garamond', 'Georgia', serif",
        fontSize: 13.5, lineHeight: 1.5, color: inkSoft,
        position: "relative", zIndex: 2,
        whiteSpace: "pre-wrap",
        maxHeight: 130, overflow: "hidden",
      }}>
        {displayDescription}
      </div>

      {/* ── Funeral / cremation details ── */}
      {displayFuneral && (
        <div style={{
          marginTop: 8, padding: "0 44px",
          textAlign: "center",
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 12, fontStyle: "italic", lineHeight: 1.4,
          color: inkAccent,
          position: "relative", zIndex: 2, whiteSpace: "pre-wrap",
        }}>
          {displayFuneral}
        </div>
      )}

      {/* ── BOTTOM band: QR on the left · text on the right ── */}
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        padding: "12px 22px",
        background: template === "charcoal" ? "rgba(0,0,0,0.35)" : "rgba(255,248,236,0.85)",
        borderTop: template === "charcoal"
          ? "1px solid rgba(199,168,103,0.35)"
          : "1px solid rgba(122,74,31,0.2)",
        display: "flex", alignItems: "center", gap: 14, zIndex: 3,
      }}>
        {/* Left: QR */}
        <div style={{
          width: 92, height: 92, borderRadius: 8,
          background: "#fff", padding: 4,
          display: "flex", alignItems: "center", justifyContent: "center",
          flex: "0 0 auto",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR" crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <div style={{ fontSize: 9, color: "#666", textAlign: "center" }}>QR<br/>loading…</div>
          )}
        </div>
        {/* Right: URL + footer message + verse */}
        <div style={{ minWidth: 0, flex: 1 }}>
          {displayFooter && (
            <div style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic", fontSize: 12, marginBottom: 3,
              color: inkAccent,
            }}>{displayFooter}</div>
          )}
          {card?.verse && (
            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 10.5, lineHeight: 1.35, whiteSpace: "pre-wrap",
              color: template === "charcoal" ? "#B8A278" : "#6B4A2E",
              marginBottom: 4,
            }}>{card.verse}</div>
          )}
          <div style={{
            fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase",
            color: inkAccent,
          }}>Scan to read tributes & leave a message</div>
          <div style={{
            fontFamily: "'JetBrains Mono', 'Menlo', monospace",
            fontSize: 11, marginTop: 1, wordBreak: "break-all",
            color: inkPrimary,
          }}>
            {(card?.share_short_url || "").replace(/^https?:\/\//, "")}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal((
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="beautifier-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(20, 15, 10, 0.55)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}
    >
      <div
        style={{
          background: "#FFF8EC", borderRadius: 16, padding: "18px 20px",
          maxWidth: 640, width: "100%", position: "relative",
          boxShadow: "0 30px 80px -30px rgba(0,0,0,0.5)",
        }}
      >
        <button
          onClick={onClose}
          data-testid="beautifier-close"
          aria-label="Close"
          style={{
            position: "absolute", right: 10, top: 10,
            background: "transparent", border: "none", color: "#8A5B2A", cursor: "pointer",
          }}
        >
          <X className="w-5 h-5" />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Wand2 className="w-5 h-5" style={{ color: "#C2410C" }} />
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 22, color: "#3B1F0A",
          }}>Beautify with AI</div>
        </div>
        <div style={{ fontSize: 12, color: "#7A4A1F", marginBottom: 12 }}>
          Generate a ceremonial card with a scannable QR that brings visitors back to leave tributes.
        </div>

        {/* Controls */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {TEMPLATES.map(t => (
            <button
              key={t.id}
              onClick={() => setTemplate(t.id)}
              data-testid={`beautifier-template-${t.id}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                border: template === t.id ? "2px solid #C2410C" : "1px solid rgba(122,74,31,0.25)",
                background: template === t.id ? "#FFF" : "transparent",
                borderRadius: 999, padding: "6px 12px",
                fontSize: 12, color: "#3B1F0A", cursor: "pointer",
              }}
            >
              <span style={{
                display: "inline-block", width: 14, height: 14, borderRadius: "50%",
                background: `linear-gradient(135deg, ${t.swatch[0]}, ${t.swatch[1]})`,
                boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.1)",
              }}/>
              {t.label}
            </button>
          ))}
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            data-testid="beautifier-language"
            style={{
              border: "1px solid rgba(122,74,31,0.25)",
              borderRadius: 999, padding: "6px 12px", fontSize: 12, background: "#FFF",
              color: "#3B1F0A",
            }}
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>

        {/* Edit fields — collapsible so it doesn't dominate the modal.
            Any field the family fills overrides the AI's output; blank
            fields fall back to what's on the memorial doc. */}
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setShowFields((v) => !v)}
            data-testid="beautifier-toggle-fields"
            style={{
              width: "100%",
              display: "inline-flex", alignItems: "center", justifyContent: "space-between",
              border: "1px solid rgba(122,74,31,0.25)",
              background: showFields ? "#FFF" : "#FFF8EC",
              borderRadius: 12, padding: "8px 12px",
              fontSize: 12, color: "#3B1F0A", cursor: "pointer",
            }}
          >
            <span style={{ fontWeight: 600 }}>Edit the card content</span>
            <span style={{ fontSize: 11, color: "#8A5B2A" }}>
              {showFields ? "Hide fields ▲" : "Name, dates, description, funeral… ▼"}
            </span>
          </button>
          {showFields && (
            <div
              data-testid="beautifier-fields"
              style={{
                marginTop: 8, padding: 12, borderRadius: 12,
                background: "#FFFFFF", border: "1px solid rgba(122,74,31,0.2)",
                display: "grid", gap: 8,
              }}
            >
              <BeautifyField label="Full name" testid="field-full-name">
                <input
                  value={fields.full_name}
                  onChange={(e) => setField("full_name", e.target.value)}
                  data-testid="beautifier-field-full-name"
                  style={_inputStyle}
                  placeholder="Full name as it should appear on the card"
                />
              </BeautifyField>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <BeautifyField label="Date / year of birth">
                  <input
                    value={fields.date_of_birth}
                    onChange={(e) => setField("date_of_birth", e.target.value)}
                    data-testid="beautifier-field-dob"
                    style={_inputStyle}
                    placeholder="e.g. 1962 or 12 Mar 1962"
                  />
                </BeautifyField>
                <BeautifyField label="Date / year of death">
                  <input
                    value={fields.date_of_death}
                    onChange={(e) => setField("date_of_death", e.target.value)}
                    data-testid="beautifier-field-dod"
                    style={_inputStyle}
                    placeholder="e.g. 2026 or 22 Aug 2026"
                  />
                </BeautifyField>
              </div>
              <BeautifyField label="Location">
                <input
                  value={fields.location}
                  onChange={(e) => setField("location", e.target.value)}
                  data-testid="beautifier-field-location"
                  style={_inputStyle}
                  placeholder="Kottayam, Kerala, India"
                />
              </BeautifyField>
              <BeautifyField label={<>S/o or D/o (parent names)</>}>
                <input
                  value={fields.parent_names}
                  onChange={(e) => setField("parent_names", e.target.value)}
                  data-testid="beautifier-field-parents"
                  style={_inputStyle}
                  placeholder="S/o Late Mr. X &amp; Mrs. Y"
                />
              </BeautifyField>
              <BeautifyField label="Description (family, life story, obituary details)">
                <textarea
                  value={fields.description}
                  onChange={(e) => setField("description", e.target.value)}
                  data-testid="beautifier-field-description"
                  style={{ ..._inputStyle, minHeight: 78, resize: "vertical" }}
                  placeholder="Beloved husband of Anitha, father of Rahul &amp; Riya… (this becomes the tribute the AI polishes)"
                />
              </BeautifyField>
              <BeautifyField label="Funeral / cremation details">
                <textarea
                  value={fields.funeral_details}
                  onChange={(e) => setField("funeral_details", e.target.value)}
                  data-testid="beautifier-field-funeral"
                  style={{ ..._inputStyle, minHeight: 52, resize: "vertical" }}
                  placeholder="Funeral: 24 Aug, 10 am at St. Mary's Church. Burial to follow."
                />
              </BeautifyField>
              <BeautifyField label="Footer message (short line at the bottom)">
                <input
                  value={fields.footer_message}
                  onChange={(e) => setField("footer_message", e.target.value)}
                  data-testid="beautifier-field-footer"
                  style={_inputStyle}
                  placeholder="Ever loved. Ever missed. Ever cherished."
                />
              </BeautifyField>
              <div style={{ fontSize: 11, color: "#7A4A1F" }}>
                Tap <b>Regenerate</b> to rebuild the card with your edits.
                Blank fields keep whatever we already had on file.
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div style={{
          display: "flex", justifyContent: "center", padding: "8px 0 16px",
          overflow: "auto",
        }}>
          {CardBody}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button
            onClick={() => generate({})}
            disabled={loading || !canBeautify}
            data-testid="beautifier-regenerate"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              border: "1px solid rgba(122,74,31,0.25)",
              background: "#FFF", color: "#3B1F0A",
              borderRadius: 999, padding: "8px 14px",
              fontSize: 13, cursor: loading ? "wait" : "pointer",
            }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            {card ? "Regenerate" : "Generate"}
          </button>
          <button
            onClick={shareToWhatsApp}
            disabled={!card}
            data-testid="beautifier-share-wa"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#25D366", color: "#fff",
              border: "none", borderRadius: 999, padding: "8px 14px",
              fontSize: 13, cursor: card ? "pointer" : "not-allowed",
              opacity: card ? 1 : 0.5,
            }}
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={downloadPng}
            disabled={!card || downloading}
            data-testid="beautifier-download"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#C2410C", color: "#fff",
              border: "none", borderRadius: 999, padding: "8px 14px",
              fontSize: 13, cursor: card && !downloading ? "pointer" : "wait",
              opacity: card ? 1 : 0.5,
            }}
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PNG
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

/* ─── Field helpers for the edit-fields panel ─────────────────────── */
const _inputStyle = {
  width: "100%", boxSizing: "border-box",
  border: "1px solid rgba(122,74,31,0.25)",
  background: "#FFF8EC", color: "#3B1F0A",
  borderRadius: 8, padding: "8px 10px",
  fontSize: 12, outline: "none",
};

function BeautifyField({ label, children, testid }) {
  return (
    <label data-testid={testid || undefined} style={{ display: "block" }}>
      <span style={{
        display: "block", fontSize: 10, letterSpacing: 1.4,
        textTransform: "uppercase", color: "#7A4A1F", marginBottom: 3,
      }}>{label}</span>
      {children}
    </label>
  );
}

/* ─── Themes ─────────────────────────────────────────────────────── */
const cardTheme = {
  cream: {
    background:
      "linear-gradient(180deg, #FDF6E4 0%, #FBE9C6 62%, #F1D9A6 100%)",
    color: "#3B1F0A",
    border: "1px solid rgba(122,74,31,0.25)",
  },
  charcoal: {
    background:
      "radial-gradient(1200px 800px at 50% -30%, rgba(199,168,103,0.15), transparent 60%)," +
      "linear-gradient(180deg, #1A1815 0%, #0d0b09 100%)",
    color: "#F0E4C6",
    border: "1px solid rgba(199,168,103,0.35)",
  },
};

/* ─── Floral overlays (as data-URL <img> so html2canvas rasterises them) ─ */
function _svgToDataUrl(svg) {
  // html2canvas rasterises base64-encoded data URLs more reliably than
  // percent-encoded ones — the byte form matches how the browser fetches
  // regular images.
  try {
    // btoa doesn't tolerate unicode; encode to UTF-8 first via TextEncoder.
    const bytes = new TextEncoder().encode(svg);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return `data:image/svg+xml;base64,${btoa(bin)}`;
  } catch (_) {
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }
}

/**
 * html2canvas skips <img src="data:image/svg+xml;...">. Convert every
 * data-svg-floral image inside `node` into an equivalent PNG data URL
 * by drawing it through an offscreen <canvas>. Runs in place so no
 * layout shift occurs.
 */
async function _prerasteriseFlorals(node) {
  const imgs = Array.from(node.querySelectorAll("img[data-svg-floral]"));
  await Promise.all(imgs.map(async (img) => {
    if (!img.src.startsWith("data:image/svg+xml")) return;
    const w = Math.max(1, Math.round(img.width || 200));
    const h = Math.max(1, Math.round(img.height || 200));
    try {
      const svgUrl = img.src;
      // Load into an Image so we can draw it into a canvas.
      const loaded = await new Promise((res, rej) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload  = () => res(i);
        i.onerror = rej;
        i.src = svgUrl;
      });
      const c = document.createElement("canvas");
      c.width  = w * 2;
      c.height = h * 2;
      const ctx = c.getContext("2d");
      ctx.drawImage(loaded, 0, 0, c.width, c.height);
      img.src = c.toDataURL("image/png");
    } catch (e) {
      // If it fails, leave the SVG in place — worst case the export
      // skips the flower and we still get a valid card.
      console.warn("[AiBeautifier] flower rasterise failed", e);
    }
  }));
}

function FloralOverlays({ template }) {
  const stroke = template === "charcoal" ? "rgba(199,168,103,0.45)" : "rgba(122,74,31,0.4)";
  const leaf   = template === "charcoal" ? "#5A6B3A" : "#6b7c4a";

  const lilySvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <g fill="#ffffff" stroke="${stroke}" stroke-width="0.7">
    <path d="M100,40 C120,55 130,80 118,100 C108,88 100,80 100,60 Z" />
    <path d="M100,40 C80,55 70,80 82,100 C92,88 100,80 100,60 Z" />
    <path d="M118,100 C140,110 150,130 130,145 C115,132 108,120 118,100 Z" />
    <path d="M82,100 C60,110 50,130 70,145 C85,132 92,120 82,100 Z" />
    <path d="M100,80 C110,110 105,140 100,150 C95,140 90,110 100,80 Z" />
    <circle cx="60" cy="70"  r="2.6" />
    <circle cx="150" cy="72" r="2.2" />
    <circle cx="42" cy="120" r="1.8" />
    <circle cx="168" cy="120" r="2.4" />
    <circle cx="72" cy="160" r="1.6" />
  </g>
  <path d="M100,150 C100,170 95,185 90,200" stroke="${leaf}" stroke-width="2.2" fill="none" />
  <path d="M92,175 C82,175 74,180 68,188" stroke="${leaf}" stroke-width="2" fill="none" />
</svg>`;

  const roseSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <path d="M40,120 C30,140 40,170 70,170 C55,150 55,135 40,120 Z" fill="${leaf}" opacity="0.6" stroke="${stroke}" stroke-width="0.6" />
  <path d="M70,140 C80,155 75,175 55,180 C60,165 60,150 70,140 Z" fill="${leaf}" opacity="0.5" stroke="${stroke}" stroke-width="0.6" />
  <circle cx="95" cy="140" r="14" fill="#F7C6C6" stroke="${stroke}" stroke-width="0.6" />
  <circle cx="95" cy="140" r="9"  fill="#EFA0A0" />
  <circle cx="95" cy="140" r="4"  fill="#7A2020" opacity="0.55" />
</svg>`;

  return (
    <>
      <img
        src={_svgToDataUrl(lilySvg)}
        alt=""
        data-svg-floral="lily"
        crossOrigin="anonymous"
        style={{
          position: "absolute", right: -20, top: -30,
          width: 220, height: 220, opacity: 0.95, zIndex: 1,
          pointerEvents: "none",
        }}
      />
      <img
        src={_svgToDataUrl(roseSvg)}
        alt=""
        data-svg-floral="rose"
        crossOrigin="anonymous"
        style={{
          position: "absolute", left: -30, bottom: 90,
          width: 180, height: 180, opacity: 0.85, zIndex: 1,
          pointerEvents: "none",
        }}
      />
      {/* Frame highlights */}
      <div style={{
        position: "absolute", inset: 12,
        border: template === "charcoal"
          ? "1px solid rgba(199,168,103,0.3)"
          : "1px solid rgba(122,74,31,0.22)",
        borderRadius: 8,
        pointerEvents: "none",
        zIndex: 1,
      }} />
    </>
  );
}
